import { useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db96pks6oi3g3tv8utag";

const contactInfo = [
  {
    icon: "ri-mail-send-line",
    title: "意见反馈邮箱",
    value: "support@tiantang-movie.com",
    desc: "内容纠错、合作洽谈与版权事宜欢迎来信",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "用户服务",
    value: "400-860-1024",
    desc: "工作日 9:00 - 18:00（法定节假日除外）",
  },
  {
    icon: "ri-map-pin-line",
    title: "办公地址",
    value: "北京市朝阳区影视产业园 A 座",
    desc: "来访请提前预约，我们恭候你的到来",
  },
];

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactPage() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "联系我们 - 天堂电影网",
    description:
      "欢迎联系天堂电影网。无论是内容反馈、合作洽谈还是版权事宜，都可以通过反馈表单或邮箱与我们取得联系。",
    keywords: "天堂电影网联系方式,联系我们,意见反馈,合作洽谈,版权联系",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, value as string));

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const lowered = String(serverMsg || "").toLowerCase();
      const isInvalid = !response.ok || code !== "OK" || lowered.includes("spam");

      if (isInvalid) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请检查填写内容后重试。"
        );
        return;
      }

      setStatus("success");
      formEl.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <SiteHeader />
      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full px-4 md:px-8 pt-10 md:pt-14 pb-8">
          <div className="max-w-[1400px] mx-auto">
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500/10 border border-accent-500/40 px-3 py-1 text-xs text-accent-300">
              <i className="ri-chat-3-line" aria-hidden="true" />
              Contact
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-4xl font-bold text-foreground-950">联系我们</h1>
            <p className="mt-2 text-sm text-foreground-400 max-w-2xl">
              对天堂电影网有任何建议、问题或合作意向？填写下方的反馈表单，或通过邮件与我们取得联系，我们会尽快回复。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-4 md:py-8">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 rounded-lg bg-background-100 ring-1 ring-background-200/70 p-6 md:p-8">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">意见反馈</h2>
              <p className="mt-2 text-sm text-foreground-500">带 <span className="text-accent-400">*</span> 的为必填项</p>

              <form id="contact-form" data-readdy-form="contact-form" onSubmit={handleSubmit} className="mt-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-800 mb-2">
                      姓名 <span className="text-accent-400">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      placeholder="请输入你的姓名"
                      className="w-full h-11 rounded-md bg-background-50 border border-background-300/60 px-3.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-800 mb-2">
                      邮箱 <span className="text-accent-400">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      placeholder="请输入你的邮箱"
                      className="w-full h-11 rounded-md bg-background-50 border border-background-300/60 px-3.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-800 mb-2">
                    留言内容 <span className="text-accent-400">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的建议或问题（最多 500 字）"
                    className="w-full rounded-md bg-background-50 border border-background-300/60 px-3.5 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="contact-guard-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                        提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill" aria-hidden="true" />
                        提交反馈
                      </>
                    )}
                  </button>
                  {status === "success" && (
                    <p className="inline-flex items-center gap-2 text-sm text-accent-300">
                      <i className="ri-checkbox-circle-fill text-accent-400" aria-hidden="true" />
                      感谢你的反馈，我们已收到你的留言！
                    </p>
                  )}
                  {status === "error" && (
                    <p className="inline-flex items-center gap-2 text-sm text-foreground-800">
                      <i className="ri-error-warning-fill text-accent-500" aria-hidden="true" />
                      {formError}
                    </p>
                  )}
                </div>
              </form>
            </div>

            <div className="space-y-4">
              {contactInfo.map((info) => (
                <div key={info.title} className="rounded-lg bg-background-100 ring-1 ring-background-200/70 p-5">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-800 text-accent-300 text-xl">
                    <i className={info.icon} aria-hidden="true" />
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-900">{info.title}</h3>
                  <p className="mt-1 text-base font-medium text-accent-300 break-all">{info.value}</p>
                  <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{info.desc}</p>
                </div>
              ))}
              <div className="rounded-lg bg-secondary-900/50 ring-1 ring-secondary-700/60 p-5">
                <div className="flex items-start gap-3">
                  <i className="ri-time-line text-lg text-accent-400 mt-0.5" aria-hidden="true" />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">回复时效</h3>
                    <p className="mt-1 text-xs text-foreground-400 leading-relaxed">
                      我们通常在 1-2 个工作日内回复用户来信。涉及版权的事宜，请在邮件中附上相关权利证明。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}